import { Component, Input, OnDestroy, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { remove as _remove, uniq as _uniq } from 'lodash';
import { catchError, map, Observable, of, shareReplay, Subscription, tap } from 'rxjs';
import { ConsumptionFormtype } from '../../../../../shared/formtypes/consumption.formtype';
import { ReBaseData } from '../../../../../shared/utils/re-base-data';
import { Utilities } from '../../../../../shared/utils/utilities';
import { defaultValidatorProxy } from '../../../../../shared/validators/default-validator-proxy';
import { INPUT_TYPE } from '../../../../enums/inputType.enum';
import { TRACKING } from '../../../../enums/trackingParts.enum';
import { EcarCompatibility, TmhData } from '../../../../models/tmhData';
import { BdoApiService } from '../../../../services/bdo-api.service';
import { StorageService } from '../../../../services/storage.service';

@Component({
    selector: 'bdo-ecar-selection',
    templateUrl: './ecar-selection.component.html',
    styleUrl: './ecar-selection.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EcarSelectionComponent implements OnInit, OnDestroy {
  @Input() parentForm = new FormGroup<ConsumptionFormtype>({});

  public tmhData: Array<TmhData>;
  public manufacturers$: Observable<string[]> = of([]);
  public manufacturerSearchString$: Observable<string>;
  public carmodels$: Observable<string[]>;
  public carmodelSearchString$: Observable<string>;
  public wallboxes$: Observable<string[]>;
  public wallboxesSearchString$: Observable<string>;
  public sortManufacturer = Utilities.sortAutosuggest;
  public INPUT_TYPE = INPUT_TYPE;
  public NOTFOUND: string =  'Modell nicht dabei'; // Filter this out so we can add it as extra option
  public compatibilityYear: WritableSignal<undefined | string> = signal(undefined);
  public TRACKING = TRACKING;
  public incompatibilityShown: boolean;
  public compatibilityQuestionable: boolean;
  public Utilities = Utilities;
  protected reBaseData = ReBaseData;
  private subscriptions = new Subscription();

  constructor(
    private apiService: BdoApiService,
    private translateService: TranslateService
  ){}

  ngOnInit(): void {
    this.parentForm.addControl(
      'manufacturer',
      new FormControl<string>('',
        [
          defaultValidatorProxy(Validators.required, this.translateService.instant('general.validator.required')),
          this.manufacturerExistsValidator()
        ]),
    );
    this.parentForm.addControl(
      'carmodel',
      new FormControl<string>('',
        [
          defaultValidatorProxy(Validators.required, this.translateService.instant('general.validator.required')),
          this.carmodelExistsValidator()
        ])
    );
    this.manufacturerSearchString$ = this.parentForm.controls.manufacturer?.valueChanges;
    this.carmodelSearchString$ = this.parentForm.controls.carmodel?.valueChanges;
    this.subscriptions.add(this.manufacturers$.subscribe()); // intial load of manufacturers

    this.manufacturers$ = this.apiService.getTmhData().pipe(
      tap((data) => {
        this.tmhData = data;
      }),
      map((data: Array<TmhData>) => {
        // Remove duplicates, extract manufacturers and order alphabetically
        return Utilities.sortAlphabetically(_uniq(
            data.map(item => item.manufacturer)
          ));
      }),
      tap(() => {
        const manufacturer = StorageService.getChargeFlexData()?.manufacturer;
        this.parentForm.controls.manufacturer.setValue(manufacturer ?? '');
        if (manufacturer) {
          this.updateCarModels(manufacturer);
        }
        this.parentForm.controls.carmodel.setValue(StorageService.getChargeFlexData()?.carmodel ?? '');
        this.compatibilityYear.set(this.getCompatibilityInfo(this.parentForm.controls.manufacturer.value, this.parentForm.controls.carmodel.value));
      }),
      shareReplay(1)
    );

    this.apiService.getTmhDataWallboxes().pipe(
      tap(data => {
        let wallboxes = data.map(item => item.manufacturer + ' ' + item.model);
        this.wallboxes$ = of(Utilities.sortAlphabetically(wallboxes));
      }),
    ).subscribe();

    this.parentForm.addControl(
      'wallbox',
      new FormControl<string>('',
        { validators: [
            defaultValidatorProxy(Validators.required, this.translateService.instant('general.validator.required')),
          ],
          asyncValidators: [
            this.wallboxExistsValidator()
          ]
        })
    );
    if ( StorageService.getChargeFlexData()?.wallbox
    ) {
      this.parentForm.controls.wallbox.setValue(StorageService.getChargeFlexData().wallbox);
    }
    this.wallboxesSearchString$ = this.parentForm.controls.wallbox?.valueChanges;

    this.subscriptions.add(this.parentForm.controls.carmodel.valueChanges.subscribe({ next: (value) => {
      const modelAndManufacturerExists = this.parentForm.controls.manufacturer.value?.length > 0 &&
        this.parentForm.controls.carmodel.value?.length > 0;
      this.compatibilityQuestionable =  modelAndManufacturerExists &&
        this.getSelectedInfo(this.parentForm.controls.manufacturer?.value, this.parentForm.controls.carmodel?.value)?.Compatibility === EcarCompatibility.WALLBOX;
      if (this.compatibilityQuestionable) {
        this.parentForm.controls.wallbox.enable();
      } else {
        this.parentForm.controls.wallbox.disable();
      }
    } }) );

    this.subscriptions.add(this.parentForm.controls.wallbox.valueChanges.subscribe({ next: (value) => {
      const selected = this.getSelectedInfo(this.parentForm.controls.manufacturer?.value, this.parentForm.controls.carmodel?.value);
      this.incompatibilityShown =
      (selected?.Compatibility === EcarCompatibility.FALSE ||
      (selected?.Compatibility === EcarCompatibility.WALLBOX && value === this.translateService.instant('tmh.consumption.wallboxNotFound')));
    } }));

  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onManufacturerSelected(selectedManufacturer: string) {
    this.parentForm.controls.manufacturer.setValue(selectedManufacturer);
    this.updateCarModels(selectedManufacturer);
    this.parentForm.controls.carmodel.setValue('');
    this.compatibilityYear.set(this.getCompatibilityInfo(selectedManufacturer, this.parentForm.controls.carmodel.value));
  }

  public updateCarModels(selectedManufacturer: string) {
    const models = this.tmhData.filter(item => item.manufacturer === selectedManufacturer).
      map(item => item.model).
      filter(item => item !== this.NOTFOUND).
      sort((a,b) =>  (a > b ? 1 : -1));
    this.carmodels$ = of(
      models
    );
    this.parentForm.controls.carmodel.updateValueAndValidity();
  }

  public onModelSelected(selectedModel: string) {
    this.parentForm.controls.carmodel.setValue(selectedModel);
    const selectedItem = this.getSelectedInfo(this.parentForm.controls.manufacturer.value, selectedModel);
    this.compatibilityYear.set(selectedItem?.constructionYearNote_de);
    let compatibility: boolean | EcarCompatibility.WALLBOX;
    switch (selectedItem?.Compatibility) {
      case 'true':
        compatibility = true;
        this.incompatibilityShown = false;
        break;
      case 'false':
        compatibility = false;
        this.incompatibilityShown = true;
        break;
      default:
        compatibility = selectedItem?.Compatibility as any;
        this.incompatibilityShown = false; // Depends on Wallbox Value
        break;
    }
    StorageService.updateChargeFlexData({
      compatibility: compatibility
    });
  }

  public getSelectedInfo(selectedManufacturer: string, selectedModel: string): TmhData {
    return this.tmhData?.find(item => item.manufacturer === selectedManufacturer && item.model === selectedModel);
  }

  public getCompatibilityInfo(selectedManufacturer: string, selectedModel: string) {
    return this.getSelectedInfo(selectedManufacturer, selectedModel)?.constructionYearNote_de;
  }

  public onWallboxSelected(selectedWallbox: string) {
    this.parentForm.controls.wallbox.setValue(selectedWallbox);
  }

    /**
   * Additional Validator to check for existing manufacturers if typed in manually
   */
  private manufacturerExistsValidator() {
    return (control: AbstractControl): { [key: string]: string | true } | null => {
      if (!control.value) {
        return null;
      }

      const errorText = this.translateService.instant('tmh.consumption.validator.manufacturer');

      if (!this.tmhData.some(item => item.manufacturer === control.value)) {
        return { ['manufacturerNotExists']: (errorText) };
      }
      return null;
    };
  }
    /**
   * Additional Validator to check for existing manufacturers if typed in manually
   */
  private carmodelExistsValidator() {
    return (control: AbstractControl): { [key: string]: string | true } | null => {
      if (!control.value) {
        return null;
      }
      const errorText = this.translateService.instant('tmh.consumption.validator.carmodel');

      if (!this.tmhData?.some(item => item.manufacturer === this.parentForm.controls.manufacturer.value && item.model === control.value)) {
        return { ['carmodelNotExists']: (errorText) };
      }
      return null;
    };
  }

    /**
   * Additional Validator to check for existing Wallbox if typed in manually
   */
    private wallboxExistsValidator(): (control: AbstractControl) => Observable<{ [key: string]: string | true } | null> {
      return (control: AbstractControl): Observable<{ [key: string]: string | true } | null> => {
        if (!control.value) {
          return of(null);
        }
        const errorText = this.translateService.instant('tmh.consumption.validator.wallbox');

        return this.wallboxes$?.pipe(
          map(wallboxes => {
            const wallboxExists = wallboxes.some(item => item === control.value) || control.value === this.translateService.instant('tmh.consumption.wallboxNotFound');
            return wallboxExists ? null : { wallboxNotExists: errorText };
          }),
          catchError(err => {
            // Handle the error and return the custom error message
            return of({ wallboxNotExists: errorText });
          })
        ) || of(null);
      };
    }
}
